import { Box, Button, Typography } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import React from 'react';
import { useSelector } from 'react-redux';
import { firstValueFrom, tap } from 'rxjs';

import { If } from 'app/components/if';
import { Abbreviation } from 'app/model/cris/abbreviations';
import { Store } from 'app/model/cris/store';
import { abbreviationService } from 'app/services/abbreviation.service';
import { selectProdDataStatus } from 'features/cris/cris.slice';

import { AbbreviationDialog } from './abbreviation-dialog';

interface StoreAbbreviationProps {
    store: Store;
}

interface AbbreviationRecord {
    id: string;
    abbreviation: string;
    sic?: string;
    model: string;
    storeName?: string;
}

export const StoreAbbreviation: React.FC<StoreAbbreviationProps> = ({ store }) => {
    const [addForm, setAddForm] = React.useState(false);
    const [abbreviations, setAbbreviations] = React.useState<AbbreviationRecord[]>([]);
    const showProd = useSelector(selectProdDataStatus);

    const columns: GridColDef[] = [
        { field: 'abbreviation', headerName: 'Abbreviation', width: 200, editable: !showProd },
        { field: 'sic', headerName: 'SIC', width: 200, editable: !showProd },
        { field: 'model', headerName: 'Model', width: 200, editable: !showProd },
    ];

    React.useEffect(() => {
        abbreviationService
            .list({}, store)
            .subscribe((response) => setAbbreviations(response.data as AbbreviationRecord[]));
    }, [store]);

    return (
        <Box>
            <Box display='flex'>
                <If condition={!showProd}>
                    <Typography variant='body1'>
                        Edit abbreviations via table (double click or focus and Enter)
                    </Typography>
                    <Box flexGrow={1} />
                    <Button variant='outlined' className='pull-right mb-1' onClick={() => setAddForm(true)}>
                        Add Abbreviation
                    </Button>
                </If>
            </Box>

            <AbbreviationDialog
                open={addForm}
                onClose={() => setAddForm(false)}
                onAddAbbreviation={(abbr: { abbreviation: string; sic: string; model: string }) => {
                    abbreviationService.save(abbr, store).subscribe((newAbbr) => {
                        setAddForm(false);
                        setAbbreviations((prev) => [...prev, newAbbr as AbbreviationRecord]);
                    });
                }}
            />

            <DataGridPro
                columns={columns}
                rows={abbreviations}
                autoHeight
                density='compact'
                paginationModel={{ pageSize: 25, page: 0 }}
                processRowUpdate={async (newRow: AbbreviationRecord) => {
                    const updated = firstValueFrom(
                        abbreviationService
                            .update(
                                {
                                    id: newRow.id,
                                    abbreviation: newRow.abbreviation,
                                    model: newRow.model,
                                    ...('string' === typeof newRow.sic && { sic: newRow.sic }),
                                } as Abbreviation,
                                store,
                            )
                            .pipe(
                                tap((abbreviation: AbbreviationRecord) => {
                                    setAbbreviations((prev) =>
                                        prev.map((row) => (row.id === abbreviation.id ? abbreviation : row)),
                                    );
                                }),
                            ),
                    );

                    return updated;
                }}
            />
        </Box>
    );
};
